import * as React from 'react';
import { observer } from 'mobx-react';
import { DataTableView, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { Button, Modal, Steps, Alert } from 'antd';
import { observable } from 'mobx';
import { TextView } from 'src/core/ui/text.view';
import { FormatService } from 'src/core/services/format.service';
import { StatusTexts } from "src/app/shared/status-texts";

const Step = Steps.Step;

interface ChangeStatusModalProps {
	quoteStatus: number;
	setBusy(isBusy: boolean);
	mode: ControlMode;
	closeStateDialog: (ev?: React.MouseEvent<HTMLButtonElement>) => void;
	showStateDialog: QuoteEntryItem;
	changeStatus: (entry: QuoteEntryItem, observations: string, confirmationNumber: string, rejectedAs?: number) => Promise<void>;
	historyDataService: DataSet<QuoteEntryStateHistory>;
	formatService: FormatService;
}

@observer
export class ChangeStatusModalView extends React.Component<ChangeStatusModalProps, {}> {
	constructor(props: Readonly<ChangeStatusModalProps>) {
		super(props);
	}
	@observable
	observations: string;

	@observable
	confirmationNumber: string;
	private handleChangeEntryToNextState = async (ev?: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
		await this.props.changeStatus(this.props.showStateDialog, this.observations || null, this.confirmationNumber || null, 0);
		this.confirmationNumber = undefined;
		this.observations = undefined;
		this.props.closeStateDialog(ev);
	};

	private handleCancelEntry = async (ev?: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
		await this.props.changeStatus(this.props.showStateDialog, this.observations || null, this.confirmationNumber || null, 4);
		this.props.closeStateDialog(ev);
	};

	private handleDenyEntry = async (ev?: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
		await this.props.changeStatus(this.props.showStateDialog, this.observations || null, this.confirmationNumber || null, this.props.showStateDialog.status === 3 ? 6 : 5);
		this.props.closeStateDialog(ev);
	};

	render() {

		let status: 'wait' | 'process' | 'error' | 'finish' = 'process';
		let description = '';
		let nextStepLabel = 'Change state: ';
		const actions = [
			<Button
				key="close"
				onClick={this.props.closeStateDialog}
			>
				Close
			</Button>
		];
		const disableChange = this.props.showStateDialog &&
			(
				this.props.showStateDialog.productType === 9
					? !this.props.showStateDialog.dynamicProperties.isPackage
					: this.props.showStateDialog.dynamicProperties.isPackage
			);
		let changeStatusButtonDisabled = this.props.quoteStatus === 0;
		let showChangeStateButton = true;
		let isEntryRejected = true;
		if (this.props.showStateDialog) {
			switch (this.props.showStateDialog.status) {
				case 0:
					description = 'Product confirmation is waiting for quote to be accept by client';
					nextStepLabel += 'Pending';
					break;
				case 1:
					description = 'Quote is accepted by client, product waiting confirmation';
					nextStepLabel += 'Requested to supplier';
					break;
				case 2:
					description = 'Salesman is processing the confirmation of this product';
					nextStepLabel += 'Confirmed';
					break;
				case 3:
					description = 'Product is confirmed or not requires confirmation';
					status = 'finish';
					break;
				case 4:
					description = 'Product is cancelled by client';
					status = 'error';
					break;
				case 5:
					description = 'Product is denied by supplier';
					status = 'error';
					break;
			}
			changeStatusButtonDisabled = disableChange || changeStatusButtonDisabled || (this.props.showStateDialog.status == 2 && (this.confirmationNumber == null || this.confirmationNumber == ''));
			showChangeStateButton = this.props.showStateDialog.status < 3;
			isEntryRejected = this.props.showStateDialog.status !== 4 && this.props.showStateDialog.status !== 5;
		}

		if (isEntryRejected) {
			actions.unshift(
				<Button
					key="cancel"
					type="danger"
					onClick={this.handleCancelEntry}
					disabled={disableChange}
				>
					Cancel by client
				</Button>
			);
			actions.unshift(
				<Button
					key="deny"
					type="danger"
					onClick={this.handleDenyEntry}
					disabled={disableChange}
				>
					{this.props.showStateDialog && this.props.showStateDialog.status === 3 ? "Cancel by supplier" : "Deny by supplier"}
				</Button>
			);
		}

		if (showChangeStateButton) {
			actions.unshift(
				<Button
					key="new-state"
					type="primary"
					disabled={changeStatusButtonDisabled}
					onClick={this.handleChangeEntryToNextState}
				>
					{nextStepLabel}
				</Button>
			);
		}

		return (
			<Modal
				width="800px"
				visible={this.props.showStateDialog != null && this.props.historyDataService != null}
				onCancel={this.props.closeStateDialog}
				title="Change state"
				maskClosable={false}
				footer={actions}
			>
				{
					this.props.quoteStatus == 0 &&
					<Alert
						message="Client must accept Terms & Conditions before processing products' confirmations"
						type="error"
						showIcon
					/>
				}
				{
					this.props.quoteStatus != 0 && this.props.showStateDialog && this.props.showStateDialog.status == 2 &&
					<TextView
						mode="edit"
						required
						label="Confirmation number"
						value={this.confirmationNumber}
						onChanged={e => this.confirmationNumber = e}
					/>
				}
				{
					this.props.quoteStatus != 0 &&
					<TextView
						mode="edit"
						rows={5}
						label="Observations"
						value={this.observations}
						onChanged={e => this.observations = e}
					/>
				}
				<br />
				<DataTableView
					rowKey="date"
					columns={[
						{
							key: 'date',
							dataIndex: 'date',
							title: 'Date',
							render: (text) => this.props.formatService.toString('small-date-time', text, "utc"),
							width: "7rem",
						},
						{
							key: 'user',
							dataIndex: 'modifiedByUserName',
							title: 'User'
						},
						{
							key: 'status',
							dataIndex: 'status',
							title: 'State',
							render: (text: number) => {
								return StatusTexts.getQuoteEntryStatusFromNumber(text);
							},
							width: "6rem",
						},
						{
							key: 'confirmationNumber',
							dataIndex: 'confirmationNumber',
							title: 'Confirmation Number',
							width: "6.5rem",
						},
						{
							key: 'observations',
							dataIndex: 'observations',
							title: 'Observations'
						}
					]}
					mode={this.props.mode}
					setBusy={this.props.setBusy}
					model={this.props.historyDataService}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideModifyButtons}
					selection={false}
				/>
				<br />
				<div style={{ marginBottom: '5px' }}>
					{description}
				</div>
				<Steps
					current={this.props.showStateDialog ? Math.min(this.props.showStateDialog.status, 4) : 0}
					status={status}
					size="small">
					<Step title="Draft" />
					<Step title="Pending" />
					<Step title="Requested to supplier" />
					<Step title="Confirmed" />
					<Step title="Failed" />
				</Steps>
			</Modal>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-entries/parts/change-status-modal.view.tsx